// Mirrors `contracts/gocardless-contracts/src/iban-supplement/ibanSupplement.contract.ts` in attempt-api
// (zod is the source of truth). The IBAN screens are reached when the bank returned an account number
// (BBAN) but no IBAN and the tenant asked to have it supplemented: the user names the country their IBAN
// starts with, the backend builds the IBAN and the user confirms it.

/** the `ibanSupplement` slice of `GET /gocardless/get-context` */
export interface IbanSupplementContext {
  /** `awaiting`: the screens have to ask; `confirmed`: already answered (a reload), move on to `next` */
  status: 'awaiting' | 'confirmed'
  /** the bank's country, to preselect; absent when the bank serves several countries */
  suggestedCountryCode?: string
  /** where a `confirmed` session goes next */
  next?: 'incomeConfirmation' | 'redirectBack'
}

/** `POST /gocardless/preview-iban` */
export interface PreviewIbanRequest {
  countryCode: string
}

export interface PreviewIbanResponse {
  iban: string
}

/** `POST /gocardless/confirm-iban` */
export interface ConfirmIbanRequest {
  countryCode: string
  iban: string
}

export interface ConfirmIbanResponse {
  /** the session pauses once more, on the income confirmation screen */
  incomeConfirmationRequired: boolean
}

/** `POST /gocardless/abort-iban` takes no body and answers with an empty object */

/**
 * the `error` of a 400 (or 404) of the IBAN endpoints, next to a human-readable `message`: why no IBAN can
 * be built from the account number and the chosen country, or that the session is past the question
 */
export type IbanSupplementErrorCode =
  | 'invalidRequest'
  | 'unknownCountry'
  | 'lengthMismatch'
  | 'formatMismatch'
  | 'checkDigitMismatch'
  | 'countryMismatch'
  | 'invalidCharacters'
  | 'ibanMismatch'
  | 'accountNumberMissing'
  | 'notAwaiting'
  | 'alreadyConfirmed'

/** the codes that mean "choose another country": the account number does not fit the one chosen */
export const IBAN_COUNTRY_ERRORS: ReadonlySet<IbanSupplementErrorCode> =
  new Set<IbanSupplementErrorCode>([
    'unknownCountry',
    'lengthMismatch',
    'formatMismatch',
    'checkDigitMismatch',
    'countryMismatch',
    'invalidCharacters',
  ])

/** `GET /gocardless/list-countries` */
export interface GocardlessCountry {
  code: string
  countryName: string
  countryNameEN: string
  countryIcon: string
}
